/**
 * studentScreensApi.js
 * --------------------
 * Production API client for Student sub-screens (Receipts, Installments, Attendance, etc.)
 * 100% Zero Mock Data - Direct FastAPI Integration matching Payroll Architecture.
 */
import api from '../../../../api/client'

// ── Biometric ──────────────────────────────────────────────────────────────
export async function getBiometricRecords({ fromDate, toDate, studentId }) {
  const { data } = await api.get('/admission/biometric', {
    params: { fromDate, toDate, studentId },
  })
  return data
}

// ── Attendance Calendar ───────────────────────────────────────────────────
export async function getAttendanceCalendar(studentId, year, month) {
  const { data } = await api.get(`/admission/attendance/student/${studentId}/calendar`, {
    params: { year, month },
  })
  return data
}

// ── Messages ───────────────────────────────────────────────────────────────
export async function getStudentMessages({ studentId, fromDate, toDate }) {
  const { data } = await api.get('/admission/messages', {
    params: { studentId, fromDate, toDate },
  })
  return data
}

// ── Ledger ─────────────────────────────────────────────────────────────────
export async function getStudentLedger(studentId) {
  const { data } = await api.get(`/admission/ledger/${studentId}`)
  return data
}

export async function getStudentLedgerWithInstallments(studentId) {
  const { data } = await api.get(`/admission/ledger/${studentId}/with-installments`)
  return data
}

// ── Receipts & Installments ────────────────────────────────────────────────
export async function getStudentReceipts(studentId) {
  if (!studentId || studentId === 'undefined' || studentId === 'null') {
    return []
  }
  const { data } = await api.get('/admission/receipts', { params: { studentId } })
  return Array.isArray(data) ? data : []
}

export async function getStudentInstallments(studentId) {
  if (!studentId || studentId === 'undefined' || studentId === 'null') {
    return []
  }
  const { data } = await api.get('/admission/installments', { params: { studentId } })
  return Array.isArray(data) ? data : []
}

export async function createStudentReceipt(payload) {
  const { data } = await api.post('/admission/receipts', payload)
  return data
}

export async function openStudentReceipt(receiptId) {
  try {
    const res = await api.get(`/admission/receipts/${receiptId}/print`, { responseType: 'blob' })
    if (res && res.data) {
      const url = URL.createObjectURL(res.data)
      window.open(url, '_blank')
      return url
    }
  } catch (e) {
    console.error('Failed to open receipt print view:', e)
  }
}

export async function downloadStudentReceipt(receiptId) {
  const res = await api.get(`/admission/receipts/${receiptId}/print`, { responseType: 'blob' })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `Receipt-${receiptId}.pdf`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1500)
  return `Receipt-${receiptId}.pdf`
}

// ── Inventory ──────────────────────────────────────────────────────────────
export async function getStudentInventory(studentId) {
  const { data } = await api.get(`/admission/students/${studentId}/inventory`)
  return data || []
}

export async function addStudentInventory(studentId, payload) {
  const { data } = await api.post(`/admission/students/${studentId}/inventory`, payload)
  return data
}

export async function deleteStudentInventory(studentId, itemId) {
  const { data } = await api.delete(`/admission/students/${studentId}/inventory/${itemId}`)
  return data
}

// ── Performance ────────────────────────────────────────────────────────────
export async function getStudentPerformance(studentId) {
  const { data } = await api.get(`/admission/students/${studentId}/performance`)
  return data || []
}

export async function addStudentPerformance(studentId, payload) {
  const { data } = await api.post(`/admission/students/${studentId}/performance`, payload)
  return data
}

export async function deleteStudentPerformance(studentId, perfId) {
  const { data } = await api.delete(`/admission/students/${studentId}/performance/${perfId}`)
  return data
}

// ── Remarks ────────────────────────────────────────────────────────────────
export async function getStudentRemarks(studentId) {
  const { data } = await api.get(`/admission/students/${studentId}/remarks`)
  return data || []
}

export async function addStudentRemark(studentId, payload) {
  const { data } = await api.post(`/admission/students/${studentId}/remarks`, payload)
  return data
}

export async function deleteStudentRemark(studentId, remarkId) {
  const { data } = await api.delete(`/admission/students/${studentId}/remarks/${remarkId}`)
  return data
}

// ── Documents ──────────────────────────────────────────────────────────────
export async function getStudentDocuments(studentId) {
  const { data } = await api.get(`/admission/students/${studentId}/documents`)
  return data || []
}

export async function uploadStudentDocument(studentId, file) {
  const fd = new FormData()
  fd.append('file', file)
  const { data } = await api.post(`/admission/students/${studentId}/documents`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function deleteStudentDocument(studentId, docId) {
  const { data } = await api.delete(`/admission/students/${studentId}/documents/${docId}`)
  return data
}

// ── Profile photo ──────────────────────────────────────────────────────────
export async function uploadStudentPhoto(studentId, file) {
  const fd = new FormData()
  fd.append('file', file)
  const { data } = await api.post(`/admission/students/${studentId}/photo`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

// ── Report cards ───────────────────────────────────────────────────────────
export async function getReportCards(studentId) {
  const { data } = await api.get(`/admission/students/${studentId}/reportcards`)
  return data || []
}

export async function getReportTerms(studentId) {
  const { data } = await api.get(`/admission/students/${studentId}/reportcards/terms`)
  return data || []
}

// ── Generate password / send report email ─────────────────────────────────
export async function generateStudentPassword(studentId, type = 'auto') {
  const { data } = await api.post(`/admission/students/${studentId}/password/generate`, { type })
  return data
}

export async function sendStudentPassword(studentId, payload) {
  const { data } = await api.post(`/admission/students/${studentId}/password/send`, payload)
  return data
}

export async function sendReportEmail(studentId, payload) {
  const { data } = await api.post(`/admission/students/${studentId}/password/email-report`, payload)
  return data
}

export default {
  getBiometricRecords,
  getAttendanceCalendar,
  getStudentMessages,
  getStudentLedger,
  getStudentLedgerWithInstallments,
  getStudentReceipts,
  getStudentInstallments,
  createStudentReceipt,
  openStudentReceipt,
  downloadStudentReceipt,
  getStudentInventory,
  addStudentInventory,
  deleteStudentInventory,
  getStudentPerformance,
  addStudentPerformance,
  deleteStudentPerformance,
  getStudentRemarks,
  addStudentRemark,
  deleteStudentRemark,
  getStudentDocuments,
  uploadStudentDocument,
  deleteStudentDocument,
  uploadStudentPhoto,
  getReportCards,
  getReportTerms,
  generateStudentPassword,
  sendStudentPassword,
  sendReportEmail,
}
